import { mioAgentMessageSchema, mioArtifactSchema } from '@mio/shared';

import type { MioAgentEvent, MioAgentMessage } from '@mio/shared';

function assistant(event: MioAgentEvent): MioAgentMessage {
  return {
    id: `stream-${event.turnId ?? event.id}`,
    role: 'mio',
    content: '',
    createdAt: event.createdAt,
    ...(event.turnId ? { turnId: event.turnId } : {}),
    modality: 'chat',
    status: 'streaming',
    artifacts: [],
    sources: [],
  };
}

/** Replayed events are deduplicated by the session before reaching this reducer. */
export function applyMioEvent(
  messages: MioAgentMessage[],
  event: MioAgentEvent,
): MioAgentMessage[] {
  if (event.type === 'message.completed' || event.type === 'turn.started') {
    const parsed = mioAgentMessageSchema.safeParse(event.payload.message);
    if (!parsed.success) return messages;
    const message = parsed.data;
    const index = messages.findIndex(
      (item) =>
        item.id === message.id ||
        (Boolean(message.turnId) && item.turnId === message.turnId && item.role === message.role) ||
        (typeof event.payload.requestId === 'string' && item.id === event.payload.requestId),
    );
    if (index < 0) return [...messages, message];
    return messages.map((item, position) => (position === index ? message : item));
  }
  if (!event.turnId) return messages;
  const index = messages.findIndex((item) => item.turnId === event.turnId && item.role === 'mio');
  const existing = index < 0 ? assistant(event) : messages[index];
  if (!existing) return messages;
  let updated: MioAgentMessage;
  if (event.type === 'text.delta' && typeof event.payload.text === 'string') {
    if (existing.status !== 'streaming') return messages;
    updated = { ...existing, content: existing.content + event.payload.text };
  } else if (event.type === 'artifact.created') {
    const parsed = mioArtifactSchema.safeParse(event.payload.artifact);
    if (!parsed.success) return messages;
    updated = {
      ...existing,
      artifacts: [...existing.artifacts.filter((item) => item.id !== parsed.data.id), parsed.data],
    };
  } else if (
    event.type === 'turn.cancelled' ||
    event.type === 'turn.failed' ||
    event.type === 'turn.completed'
  ) {
    if (index < 0) return messages;
    updated = {
      ...existing,
      status:
        event.type === 'turn.failed'
          ? 'failed'
          : event.type === 'turn.cancelled'
            ? 'interrupted'
            : 'complete',
    };
  } else return messages;
  return index < 0
    ? [...messages, updated]
    : messages.map((item, position) => (position === index ? updated : item));
}
